import React from 'react';
import moment from 'moment';
import { get } from 'lodash/object';
import { isArray, isEqual } from 'lodash/lang';
import { uniq } from 'lodash/array';
import { Collection } from 'backbone';

import Base from 'app/views/base';
import BaseModal from 'app/views/modals/base_modal';

import Shipment from 'app/models/shipment';

import AddressForm from 'app/components/model_forms/address';
import ParcelForm from 'app/components/model_forms/parcel';
import CustomsInfoForm from 'app/components/model_forms/customs_info';
import ShipmentForm from 'app/components/model_forms/shipment';

import { showResponseError, handleResponseError } from 'utils/messages';
import { trimMoney } from 'utils/strings';

import tmpl from './template';
import tabsTmpl from './tabs_template';
import chooseLabelTmpl from './choose_label_template';

export class Tab {
  constructor(text, label, success, error) {
    this.text = text;
    this.label = label;

    this.success = success;
    this.error = error;
  }
}

export const TabsView = Base.extend({
  compiledTemplate: tabsTmpl,

  initialize({ tabs = [], prefix = '' }) {
    this.tabs = tabs;
    this.prefix = prefix;
    this.activeIndex = 0;
  },

  setTabStatus(label, success, error) {
    const tab = this.tabs.find(t => t.label === label);
    this.activeIndex = this.$el.find('a.active').index();

    tab.success = success;
    tab.error = error;

    this.render();
  },

  context() {
    const {
      tabs,
      prefix,
      activeIndex,
    } = this;

    return { tabs, prefix, activeIndex };
  },

  afterRender() {
    this.$el.find('[data-toggle="tooltip"]').tooltip();
  },
});

export const ChooseLabelView = Base.extend({
  compiledTemplate: chooseLabelTmpl,

  events: {
    'click .buy-label': 'purchaseLabel',
    'change #carrier-list': 'updateCurrentCarrier',
  },

  buying: false,

  initialize() {
    this.updateAvailableCarrierList(this.model.get('rates'));

    this.model.on('sync change request', () => this.resync());
    this.model.get('to_address').on('sync', () => this.resync());
    this.model.get('from_address').on('sync', () => this.resync());
    this.model.get('return_address').on('sync', () => this.resync());
    this.model.get('customs_info').on('sync', () => this.resync());
    this.model.get('parcel').on('sync', () => this.resync());

    this.model.on('buy:error', this.onBuyError.bind(this));
  },

  resync() {
    this.buying = false;
    this.updateAvailableCarrierList(this.model.get('rates'));
    this.render();
  },

  updateAvailableCarrierList(rates = []) {
    this.availableCarriers = uniq(rates.map(r => r.carrier));
    this.currentCarrier = this.availableCarriers.includes('USPS') ? 'USPS' : this.availableCarriers[0];
  },

  updateCurrentCarrier() {
    this.currentCarrier = this.$el.find('#carrier-list').val();
    this.render();
  },

  onBuyError() {
    this.buying = false;
    this.render();
  },

  purchaseLabel(e) {
    e.preventDefault();
    if (this.buying) { return; }

    const rateId = $(e.currentTarget).data().rate;
    this.buying = rateId;
    this.model.buy(rateId);
    this.render();
  },

  formatRates(carrier) {
    return (this.model.get('rates') || [])
      .filter(r => r.carrier === carrier)
      .sort((a, b) => Number(a.rate) > Number(b.rate))
      .map(r => {
        const rate = { ...r };

        if (r.delivery_date) {
          rate.delivery_date = moment(rate.delivery_date).format('MMMM Do YYYY [by] h:mm:ss a');
        }

        rate.display_rate = trimMoney(rate.rate, rate.currency);

        return rate;
      });
  },

  context() {
    const shipment = this.model.toJSON(false);

    return {
      shipment,
      rates: this.formatRates(this.currentCarrier),
      currentCarrier: this.currentCarrier,
      availableCarriers: this.availableCarriers,
      buying: this.buying,
      complete: (
        get(shipment, 'to_address.id') &&
        get(shipment, 'from_address.id') &&
        get(shipment, 'parcel.id')
      ),
    };
  },
});

export default BaseModal.extend({
  compiledTemplate: tmpl,

  events: {
    'click .modal-footer button[type="submit"]': 'next',
    'click [data-dismiss="modal"]': 'triggerClose',
    'submit form': 'save',
  },

  changedCache: {},

  triggerClose(e) {
    e.preventDefault();
    this.close();
  },

  syncModelToTab(model, tabName) {
    model.on('sync', () => this.setTabStatus(tabName, true, false));
    model.on('error', (_, res) => {
      handleResponseError(res, errMsg => {
        showResponseError(res);
        this.setTabStatus(tabName, false, errMsg);
      });
    });
  },

  initialize({
    apiModeSelection,
    shipment = new Shipment({ }, { initializeNestedModels: true }),
  }) {
    this.apiModeSelection = apiModeSelection;
    this.shipment = shipment;
    this.shipment.initializeNestedModels();

    this.shipment.set({
      ...apiModeSelection.getParams(),
    });

    this.shipment.on('buy:success', res => this.onBuySuccess(res));
    this.shipment.on('buy:error', res => this.onBuyError(res));

    this.tabsView = new TabsView({
      prefix: 'shipment-create',
      tabs: [
        new Tab('To Address', 'to_address'),
        new Tab('From Address', 'from_address'),
        new Tab('Return Address', 'return_address'),
        new Tab('Parcel', 'parcel'),
        new Tab('Customs Info', 'customs_info'),
        new Tab('Options', 'shipment'),
        new Tab('Label', 'label'),
      ],
    });

    this.syncModelToTab(this.shipment, 'shipment');

    this.tabsView.tabs
      .filter(({ label }) => !['label', 'shipment'].includes(label))
      .forEach(({ label }) => {
        this.syncModelToTab(this.shipment.get(label), label);
      });

    this.addSubview('tabs', '.modal-tabs', this.tabsView);

    this.addReactComponent(
      '.to-address-form',
      <AddressForm model={this.shipment.get('to_address')} />,
    );

    this.addReactComponent(
      '.from-address-form',
      <AddressForm model={this.shipment.get('from_address')} />,
    );

    this.addReactComponent(
      '.return-address-form',
      <AddressForm model={this.shipment.get('return_address')} />,
    );

    this.addReactComponent(
      '.parcel-form',
      <ParcelForm model={this.shipment.get('parcel')} />,
    );

    this.addReactComponent(
      '.customs-info-form',
      <CustomsInfoForm model={this.shipment.get('customs_info')} />,
    );

    this.addReactComponent(
      '.shipment-form',
      <ShipmentForm model={this.shipment} />,
    );

    this.chooseLabelView = new ChooseLabelView({
      model: this.shipment,
      apiModeSelection,
    });

    this.chooseLabelView.on('save:success', () => this.setTabStatus('label', true, false));

    this.addSubview('chooseLabel', '.choose-label', this.chooseLabelView);
  },

  setTabStatus(label, success, error) {
    this.tabsView.setTabStatus(label, success, error);
    this.bindTabEvents();
  },

  bindTabEvents() {
    this.$el.find('a[data-toggle="list"]').on('hide.bs.tab', e => {
      this.saveForm(e.target.getAttribute('href'));
    });
  },

  saveForm(id) {
    const $container = this.$el.find(id);
    const subviewData = $container.find('[data-subview],[data-model]').data();

    if (!subviewData) { return; }

    const model = subviewData.model === 'shipment' ? this.shipment : this.shipment.get(subviewData.model);

    if (this.isNotEmpty(model) && this.hasChanged(subviewData.model, model)) {
      this.saveModel(model);
    }
  },

  isNotEmpty(model) {
    const json = model.toJSON(false);

    if (model instanceof Collection) {
      return model.length > 0;
    }

    // If the object has nothing other than `mode`, empty array properties, and empty models,
    // ignore.
    if (Object.keys(json).filter(k => (
      k !== 'mode' &&
      (!isArray(model.get(k)) || model.get(k).length > 0) &&
      (!(model.nestedModels[k]) || this.isNotEmpty(model.get(k)))
    )).length) {
      return true;
    }

    return false;
  },

  hasChanged(property, model) {
    const oldJSON = this.changedCache[property];
    const newJSON = model.toJSON();

    if (isEqual(newJSON, oldJSON)) {
      return false;
    }

    model.once('sync', () => {
      this.changedCache[property] = model.toJSON();
    });

    return true;
  },

  saveModel(model) {
    model.set({
      id: undefined,
      ...this.apiModeSelection.getParams(),
    });

    if (model instanceof Shipment) {
      // only try to save if you have to, from, and parcel
      if (
        model.get('to_address').id &&
        model.get('from_address').id &&
        model.get('parcel').id
      ) {
        model.save();
      }
    } else {
      model.save();

      // If the shipment already had an id, resave the shipment.
      if (this.shipment.get('id')) {
        this.shipment.set({
          id: undefined,
          rates: undefined,
        });

        // Immediately nuke id/rates so that if you update address then
        // immediately go to the labels page, you see a "saving" message.
        // Then re-nuke (just in case) and re-save once the model is finished
        // syncing.
        model.once('sync', () => {
          this.saveModel(this.shipment);
        });
      }
    }
  },

  next() {
    const tabs = this.$el.find('.list-group a');
    const activeTabIndex = this.$el.find('.list-group a.active').index();

    if (activeTabIndex + 1 < tabs.length) {
      const nextTab = tabs.get(activeTabIndex + 1);
      $(nextTab).tab('show');
    }
  },

  onBuySuccess() {
    this.close();
    this.trigger('buy:success', this.shipment);
  },

  onBuyError(response) {
    this.trigger('buy:error', response, this.shipment);
  },

  afterRender() {
    this.bindTabEvents();
  },
});



// WEBPACK FOOTER //
// ./app/components/pages/shipments/create/modal/index.js